import { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../config/api";

const BOT_API = `${getApiUrl()}/api/bot-dashboard`;
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

// ─── Origin helpers (UI-side validation only; backend enforces the real rule) ──

const normalizeOrigin = (raw) => raw.trim().replace(/\/+$/, "");

// Accepts: example.com · https://example.com · https://app.example.com
//          https://*.example.com (wildcard subdomain) · http://localhost:3000
const isValidOrigin = (v) => {
  if (!v) return false;
  if (v === "*") return true; // explicit "allow all origins"
  const domainRe =
    /^(https?:\/\/)?(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}(:\d{1,5})?$/i;
  const localhostRe = /^(https?:\/\/)?localhost(:\d{1,5})?$/i;
  return domainRe.test(v) || localhostRe.test(v);
};

const CorsTab = ({ bot, token, onSaved }) => {
  const [origins, setOrigins] = useState([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // "saved" | "error" | null
  const [dirty, setDirty] = useState(false);

  // Seed from the bot record.
  useEffect(() => {
    setOrigins(
      Array.isArray(bot?.embed_allowed_origins)
        ? bot.embed_allowed_origins
        : [],
    );
    setDirty(false);
    setSaveStatus(null);
  }, [bot?.bot_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addOrigin = () => {
    const value = normalizeOrigin(input);
    if (!value) return;
    if (!isValidOrigin(value)) {
      setError("Enter a valid domain, e.g. https://example.com");
      return;
    }
    if (origins.some((o) => o.toLowerCase() === value.toLowerCase())) {
      setError("That domain is already in the list.");
      setInput("");
      return;
    }
    setOrigins([...origins, value]);
    setInput("");
    setError("");
    setDirty(true);
    setSaveStatus(null);
  };

  const removeOrigin = (value) => {
    setOrigins(origins.filter((o) => o !== value));
    setDirty(true);
    setSaveStatus(null);
  };

  const handleSave = async () => {
    // Fold any pending, valid input into the list before saving.
    let finalList = origins;
    const pending = normalizeOrigin(input);
    if (pending && isValidOrigin(pending) && !origins.includes(pending)) {
      finalList = [...origins, pending];
      setOrigins(finalList);
      setInput("");
    }

    setSaving(true);
    setSaveStatus(null);
    try {
      await axios.put(
        `${BOT_API}/${bot.bot_id}`,
        { embed_allowed_origins: finalList },
        { headers: authHeader(token) },
      );
      setSaveStatus("saved");
      setDirty(false);
      onSaved?.({ ...bot, embed_allowed_origins: finalList });
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const hasWildcard = origins.includes("*");

  return (
    <div className="space-y-6">
      {/* ── Intro ─────────────────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800">
          CORS &amp; Allowed Domains
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Only origins in this list may load this bot and call its endpoints.
          Requests from any other origin are blocked.{" "}
          <span className="font-medium text-gray-600">
            Leave the list empty to allow all origins.
          </span>
        </p>
      </div>

      {/* ── Add domain ────────────────────────────────────────── */}
      <div>
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOrigin();
              }
            }}
            placeholder="https://example.com"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500 bg-slate-100 text-gray-900"
          />
          <button
            onClick={addOrigin}
            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors shrink-0"
          >
            Add URL
          </button>
        </div>
        {error ? (
          <p className="text-xs text-red-500 mt-2">{error}</p>
        ) : (
          <p className="text-xs text-gray-400 mt-2">
            Enter the full origin, e.g.{" "}
            <code className="font-mono">https://example.com</code> (no path).
            Use <code className="font-mono">https://*.example.com</code> for all
            subdomains, or <code className="font-mono">*</code> to explicitly
            allow every origin.
          </p>
        )}
      </div>

      {/* ── Status banners: empty = allow all, or explicit wildcard ─── */}
      {origins.length === 0 ? (
        <div className="flex items-center gap-2.5 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          All origins allowed (no restrictions)
        </div>
      ) : hasWildcard ? (
        <div className="flex items-center gap-2.5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          The list contains <code className="font-mono mx-1">*</code> — all
          origins are explicitly allowed.
        </div>
      ) : null}

      {/* ── Allowed list ──────────────────────────────────────── */}
      {origins.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Allowed domains
            </span>
            <span className="text-xs text-gray-400">
              {origins.length} {origins.length === 1 ? "origin" : "origins"}
            </span>
          </div>

          <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {origins.map((origin) => {
              const isWildcard = origin === "*";
              return (
                <li
                  key={origin}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isWildcard ? "bg-blue-100" : "bg-green-100"
                      }`}
                    >
                      {isWildcard ? (
                        <svg
                          className="w-3 h-3 text-blue-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-3 h-3 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="text-sm font-mono text-gray-700 truncate">
                      {origin}
                    </span>
                  </div>
                  <button
                    onClick={() => removeOrigin(origin)}
                    title="Remove"
                    className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Security note ─────────────────────────────────────── */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800 ">
        <strong>Heads up:</strong> This is your bot's security boundary. Only
        add domains you own and trust — any listed origin can embed the widget
        and call the bot's API on your behalf. An empty list (or{" "}
        <code className="font-mono">*</code>) leaves the bot open to every
        origin.
      </div>

      {/* ── Save ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="text-sm">
          {saveStatus === "saved" ? (
            <span className="text-green-600">Saved successfully.</span>
          ) : saveStatus === "error" ? (
            <span className="text-red-500">
              Failed to save. Please try again.
            </span>
          ) : (
            <span className="text-xs text-gray-400">
              {origins.length === 0 || hasWildcard
                ? "Currently open to all origins"
                : `${origins.length} origin${origins.length > 1 ? "s" : ""} configured`}
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default CorsTab;
